import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { TrendingUp, TrendingDown, Rocket, Clock, ShieldCheck, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * RecentExits — the "we just sold X" ribbon.
 *
 * Placed at the TOP of the Dashboard so subscribers see exit outcomes the
 * moment they open the app — no more surprises later ("wait, we sold WKHS
 * yesterday and I missed it?").
 *
 * Data source: /api/exits/recent — returns the last N closed alerts with a
 * pre-computed `exit_tag` (MOON HIT / STOPPED OUT / TIME EXIT / ANALYST CLOSE)
 * so we don't have to reproduce the exit-classification logic on the client.
 *
 * Auto-polls every 60s so a subscriber who's parked on the dashboard sees a
 * new exit surface within a minute of it firing — matching the Discord/email
 * broadcast timing.
 */
export default function RecentExits({ authHeader }) {
  const [exits, setExits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () => {
      axios
        .get(`${BACKEND_URL}/api/exits/recent?limit=8&days=7`, { headers: authHeader })
        .then((r) => alive && setExits(r.data?.exits || []))
        .catch(() => {})
        .finally(() => alive && setLoading(false));
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [authHeader]);

  if (loading) return null;
  if (!exits.length) return null;

  return (
    <section
      className="mb-6 rounded-lg overflow-hidden"
      style={{
        border: "1px solid var(--mb-border)",
        background: "var(--mb-surface)",
      }}
      data-testid="recent-exits-ribbon"
    >
      <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{ borderColor: "var(--mb-border)" }}>
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          <h3 className="font-display font-bold text-sm tracking-wide" style={{ color: "var(--mb-ink)" }}>
            RECENT EXITS
          </h3>
          <span className="text-[10px] font-mono-data uppercase tracking-widest text-mb-ink-subtle">
            Last 7 days · {exits.length}
          </span>
        </div>
        <button
          className="text-xs text-mb-ink-muted hover:text-mb-ink transition"
          onClick={() => setCollapsed(!collapsed)}
          data-testid="recent-exits-toggle"
        >
          {collapsed ? "Show" : "Hide"}
        </button>
      </div>

      {!collapsed && (
        <div className="divide-y" style={{ borderColor: "var(--mb-border)" }}>
          {exits.map((e) => (
            <ExitRow key={e.id} exit={e} />
          ))}
        </div>
      )}
    </section>
  );
}

const TAG_STYLE = {
  "win-hard":  { bg: "rgba(16, 185, 129, 0.16)", fg: "#10B981", border: "#10B981", icon: Rocket },
  "win-soft":  { bg: "rgba(16, 185, 129, 0.10)", fg: "#10B981", border: "rgba(16,185,129,0.4)", icon: TrendingUp },
  "loss-hard": { bg: "rgba(239, 68, 68, 0.16)",  fg: "#EF4444", border: "#EF4444", icon: TrendingDown },
  "loss-soft": { bg: "rgba(239, 68, 68, 0.10)",  fg: "#EF4444", border: "rgba(239,68,68,0.4)", icon: TrendingDown },
  "neutral":   { bg: "rgba(148, 163, 184, 0.14)", fg: "#94A3B8", border: "rgba(148,163,184,0.4)", icon: Clock },
};

function ExitRow({ exit }) {
  const tag = exit.exit_tag || { label: "CLOSED", tone: "neutral" };
  const style = TAG_STYLE[tag.tone] || TAG_STYLE.neutral;
  const Icon = style.icon;
  const returnPct = exit.final_return_pct;
  const isWin = returnPct != null && returnPct >= 0;
  const cls = exit.alert_class || "steady";
  const isMoon = cls === "moonshot";
  const isAnalyst = (exit.closed_reason || "") === "analyst_close";
  const timeAgo = _ago(exit.closed_at);

  return (
    <Link
      to={`/app/stock/${exit.symbol}`}
      className="block px-4 py-2.5 hover:brightness-110 transition"
      style={{ background: "var(--mb-surface-elevated)" }}
      data-testid={`exit-row-${exit.symbol}`}
    >
      <div className="flex items-center gap-3 justify-between">
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{
              backgroundColor: isMoon ? "#FF4D9D" : "#A855F7",
              boxShadow: `0 0 10px ${isMoon ? "#FF4D9D" : "#A855F7"}`,
            }}
            title={isMoon ? "Moonshot" : "Steady"}
          />
          <span className="font-mono-data font-bold text-base" style={{ color: "var(--mb-ink)" }}>
            {exit.symbol}
          </span>
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono-data text-[10px] font-bold tracking-widest border"
            style={{ backgroundColor: style.bg, color: style.fg, borderColor: style.border }}
            data-testid={`exit-tag-${exit.symbol}`}
          >
            <Icon className="w-3 h-3" />
            {tag.label}
          </span>
          {isAnalyst && (
            <span
              className="hidden sm:inline-flex items-center gap-1 text-[10px] font-mono-data text-mb-ink-muted"
              title="Manual analyst close — momentum broke, we called it"
            >
              <ShieldCheck className="w-3 h-3" />
              analyst
            </span>
          )}
          {exit.sector && (
            <span className="hidden md:inline-flex text-[10px] font-mono-data text-mb-ink-subtle">
              {exit.sector_emoji || "🏷️"} {exit.sector}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {returnPct != null && (
            <span
              className="font-mono-data font-bold text-sm"
              style={{ color: isWin ? "#10B981" : "#EF4444" }}
              data-testid={`exit-return-${exit.symbol}`}
            >
              {isWin ? "+" : ""}{returnPct.toFixed(2)}%
            </span>
          )}
          <span className="text-[10px] font-mono-data text-mb-ink-subtle tabular-nums w-14 text-right">
            {timeAgo}
          </span>
          <ChevronRight className="w-3.5 h-3.5 text-mb-ink-subtle" />
        </div>
      </div>
    </Link>
  );
}

function _ago(iso) {
  if (!iso) return "—";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return "—"; }
}
